import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { CalendarPlus, CalendarX2, ChevronRight, Music, UserX, Users } from 'lucide-react'
import { clockTime, meridiem } from '../api/attendance'
import { formatFullDate, today } from '../api/calendar'
import {
  classLabel,
  fetchTeacherClasses,
  fetchTeacherHome,
} from '../api/teacher'
import { useCan, useSession } from '../useSession'
import CalendarWeekView from './CalendarWeekView'
import CalendarDayView from './CalendarDayView'
import CalendarMonthView from './CalendarMonthView'
import type { CalendarView } from './CalendarViewSwitcher'
import BookingForm from './BookingForm'
import { canDeclareOut } from '../api/cover'
import CoverOnHome, { useCoverWaiting } from './CoverOnHome'
import OpenCoverOnHome from './OpenCoverOnHome'
import AsksOnHome, { useAsksWaitingOnMe } from './AsksOnHome'
import DeclareOutDay, { refreshAfterDeclaring } from './DeclareOutDay'
import UnpaidPrivatesOnHome from './UnpaidPrivatesOnHome'
import RosterModal from './RosterModal'


/*
 * How many upcoming sessions the home list shows before it offers the rest
 * (D-371). Short on purpose; the button beside it is what makes short safe.
 */

/**
 * A teacher's own screen, on her own phone (D-128).
 *
 * **Not the classroom tablet and not the studio calendar.** The tablet knows
 * *where it is* and leads with the room, unlocked by a PIN so anyone covering
 * can use it. This knows *who she is*: her classes, her registers, her music.
 *
 * **Everything beyond that is capability-driven** (A-3, D-5). The links at the
 * foot appear because her account holds them, so Tonya granting a senior
 * teacher more simply makes more appear — there is no separate idea here of
 * what a teacher is allowed to do.
 */
export default function TeacherPortalView({ heading }: { heading?: string } = {}) {

  /*
   * The day the quiet cover line points at, or null (D-365). A date rather
   * than a boolean, because the line already knows which day it is about and
   * making her re-find it is the fault shape D-360 recorded.
   */
  const [sortingDay, setSortingDay] = useState<string | null>(null)

  /*
   * "Coming up" opens (D-371). Collapsed by default, because the owner asked
   * for a short list — but a short list that cannot be opened is one that hides
   * things, which is how a private booked for Thursday was invisible on a
   * teacher whose Tuesday already fills the cut.
   */

  const queryClient = useQueryClient()

  const { data, isPending, isError } = useQuery({
    queryKey: ['teacher-home'],
    queryFn: fetchTeacherHome,
  })

  /*
   * **Is there anything under the heading** (D-415).
   *
   * Reported on a dual-role account: *Needs an answer* sat over empty space,
   * because the heading is `HomeScreen`'s and every panel below it decides its
   * own emptiness — two of them by returning `null` outright.
   *
   * So the heading came here, where the answer is. The two child hooks read the
   * same query keys their panels do, which costs no extra request and leaves
   * each emptiness rule written once, in the panel that renders from it.
   */
  const coverWaiting = useCoverWaiting()
  const asksWaiting = useAsksWaitingOnMe()


  /*
   * <b>What is waiting on her survives her schedule not loading</b> (D-767,
   * found by an audit).
   *
   * These two returns are about <c>/api/teacher</c>, and the ask list is a
   * different request with a different key - so a failed schedule was hiding
   * rows that had loaded perfectly well. That was harmless while the family
   * half drew them too; the moment the duplicate went, this became the only
   * renderer, and a teacher whose schedule refused would have seen *Could not
   * load your schedule* and nothing about the lesson she has to log.
   *
   * <b>It is the ordinary case, not a rare one.</b> Every database-backed
   * endpoint refuses for ~75 seconds after a deploy and the client gives up
   * after about a second (D-33), so one request failing while another succeeds
   * is what a deploy looks like from her phone - and a screen must never let a
   * missing lookup speak for data it never asked about (D-209).
   *
   * No heading in these two branches: whether there is anything to head needs
   * `data`, and the panel carries its own <i>Waiting on you</i> title anyway.
   */
  if (isPending) {
    return (
      <>
        <AsksOnHome />
        <p className="px-5 py-10 text-center text-[14px] text-ink-3">Loading...</p>
      </>
    )
  }

  if (isError || !data) {
    return (
      <>
        <AsksOnHome />
        <p className="px-5 py-10 text-center text-[14px] text-crit">
          Could not load your schedule. Please try again.
        </p>
      </>
    )
  }

  /*
   * Everything this screen can say. If none of it is true there is nothing to
   * head, and a title over empty space is a promise with nothing behind it.
   */
  const anything =
    coverWaiting ||
    asksWaiting ||
    data.outstanding > 0 ||
    data.reportedOut > 0

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex-1">
      {/*
        **The heading belongs to whatever is under it** (D-415).

        Only a dual-role account gets one at all — it exists to separate her
        teacher half from her parent half (D-380), and an account with one
        relationship needs no label because there is nothing to tell apart.
      */}
      {heading && anything && (
        <h2 className="mt-6 mb-2 font-display text-lg">{heading}</h2>
      )}
      {/*
        No second greeting and no second summary (D-380). "Hi, Ashley" was on
        this page twice and `todaySummary` said in prose what the Today panel
        now says as rows — with her children in it, which the prose could not
        do.
      */}

      {/*
        Her own unfinished registers, and only hers — chasing everybody's is
        the studio's screen, not a teacher's (D-118).
      */}
      {/*
        **Cover, with the other things waiting on her rather than below her
        day** (D-365).

        Owner: *"i think burying too much stuff with scrolling on the schedule
        page ... they wouldn't see it. so i feel like it should prob live on the
        home page. when there is something to display in the cover section we
        display it else show nothing."*

        **Above the today list, which is a change to the order he proposed.**
        `todaySessions` is uncapped and includes privates and room bookings, so
        a twelve-session Monday is roughly 1,680px of cards — putting cover
        after them lands it at the same depth D-352 measured as the burying
        problem this move is escaping. Put to him with the measurement and not
        contested.
      */}
      {/*
        **Level-up makeups, beside cover** (D-403). Both are somebody waiting on
        her answer, both clear the moment she gives it, and both belong above
        her day for the reason D-365 measured: a twelve-session Monday is
        roughly 1,680px of cards, so anything below them is buried.
      */}
      {/*
        Everything asked of her, answered where it is seen (D-543, D-552): a
        colleague's class to cover, a level-up into her session, a routine's
        makeup to agree to, a task. One list, one row, on the family's Home
        and on hers.
      */}
      <AsksOnHome />

      <CoverOnHome onOpenDay={setSortingDay} />

      {/*
        **The nights nobody has, open to anybody who could take one** (D-650).

        Owner, from the pilot: *"Don't want the needs coverage to email
        everyone. Just put it out there for anyone to take as coverage. Maybe
        show on all teachers home page."*

        **Below her own cover and below what is waiting on her, deliberately.**
        Those two are obligations — a question nobody else can answer, a class
        of hers still needing somebody. This is an offer, and putting an offer
        above an obligation is how a screen teaches people to scroll past its
        own top. It is also why it is **not** part of `anything` above: that
        heading reads *Needs an answer*, and nothing here needs one.
      */}
      <OpenCoverOnHome />

      {sortingDay && (
        <DeclareOutDay
          initialDate={sortingDay}
          onClose={() => setSortingDay(null)}
          onDone={() => refreshAfterDeclaring(queryClient)}
        />
      )}

      {/*
        **The number is a link to the list now, and it says what to do.**

        Reported from testing: *"make this so you can click on it and bring you
        to what class need attendance taken for, and change wording to classes
        still needing to take attendance."* Both halves were already known —
        `navViews.ts` says in as many words that without the *To mark*
        destination "a teacher could see the number and not the list", and that
        destination existed all along. This was the sentence left behind.

        `/registers` is that list, gated on `TakeAttendance`, which is the same
        capability that produced this count — so it can never offer a screen the
        reader is refused (`CLAUDE.md`: a link that refuses the person holding
        it is worse than no link).

        *earlier* stays. These are past classes, and dropping it would leave a
        teacher unable to tell them from the ones she is about to teach tonight.
      */}
      {/*
        Privates from before the app started asking (D-864). Above the register
        notice because it is the shorter-lived of the two: the register count
        recurs every week, this list empties once and never comes back.
      */}
      <UnpaidPrivatesOnHome privates={data.unpaidPrivates ?? []} />

      {data.outstanding > 0 && (
        <Link
          to="/my-attendance"
          className="mb-4 block rounded-xl border border-warn bg-warn-bg px-4 py-2.5 text-[13px] text-ink no-underline hover:border-ink-3"
        >
          <strong>{data.outstanding}</strong>{' '}
          {data.outstanding === 1
            ? 'earlier class still needs attendance taken.'
            : 'earlier classes still need attendance taken.'}
        </Link>
      )}

      {/*
        Who is missing from the class she is about to teach (D-188).

        Counts dancers at her *next* session only. A week's worth would sit here
        for days and stop being read, and one family reporting five days off
        would show as five children — the number she acts on has to be the
        number of empty spots on the floor.
      */}
      {data.reportedOut > 0 && (
        <Link
          to="/absences"
          className="mb-4 flex items-center gap-2.5 rounded-xl border border-warn bg-warn-bg px-4 py-2.5 text-[13px] no-underline"
        >
          <UserX size={15} strokeWidth={2.5} className="shrink-0 text-warn" />
          <span className="flex-1">
            <strong>{data.reportedOut}</strong>{' '}
            {data.reportedOut === 1 ? 'dancer is' : 'dancers are'} reported out
            {data.nextSessionDate && data.nextSessionDate !== data.today
              ? ` on ${formatFullDate(data.nextSessionDate)}`
              : ' of your next class'}
            .
          </span>
          <ChevronRight size={15} strokeWidth={2.5} className="shrink-0 text-warn" />
        </Link>
      )}

      {/*
        Four tabs, the same four shapes a family gets (D-300).

        <b>"Today" and "Coming up" were two tabs and are now one.</b> They are
        the same question a day apart, and splitting them meant a teacher
        checking "what have I got" had to look in two places and remember which.
        Home answers it: what is on now, then what is next.

        <b>Schedule is the studio's own calendar</b>, not a third
        implementation. It already knows week / day / month, it is already
        scoped to her, and tapping an empty slot already opens the booking form
        — which is how she schedules a private (D-299).
      */}
      {/*
        The tabs are gone; each was a destination and now has a URL (D-322).

        **They had become the second navigation** — a row inside the content
        area, under a header that was already navigation, which is the
        complaint this rework started from. Mounting the rail while these
        stayed put two of them on a phone at once, stacked, which is how this
        came to be finished rather than deferred.

        What is left is what the tab called "Today": tonight, then what is
        next. That is this screen's job and it always was.
      */}
      {/*
        Today's classes and the fortnight are gone from Home (D-380).

        <b>The Today panel above says the same thing in one line a person</b>,
        and it says it for her children too — which this could not. Seventeen
        "Take attendance" buttons were on this screen at once, seven of them in
        the panel and ten repeating it. What is left here is what needs
        answering rather than what is happening: cover, unfinished registers,
        dancers reported out.

        <b>The list itself is not deleted.</b> `TeacherSchedule` renders the
        studio's own calendar at /schedule, which is where a fortnight belongs
        — Home is a snapshot, and a second implementation of the schedule is
        what this removes.
      */}

      {/*
        The quick links are gone (D-300).

        <b>Find a dancer and Missing attendance were here and in the header</b>,
        two taps apart on the same screen — reported from live testing. The
        header carries them on every screen, which is the better place; a copy
        underneath only trains the eye to skip both.

        "The whole studio schedule" is gone too, and it is not missed: the
        Schedule tab <i>is</i> the studio calendar now, with a toggle that
        actually widens it (D-299). The link had claimed to do that and did not.
      */}
      </div>


    </div>
  )
}

/**
 * Her schedule, which is the studio's own calendar (D-300).
 *
 * **Not a third implementation.** The parent portal renders a list because a
 * family asks *"where does my child need to be?"*; a teacher asks that **and**
 * *"what is free on Wednesday so I can put a private in it"*, and the second
 * question is a room-by-time grid. That grid already exists, is already scoped
 * to her, and already opens the booking form when she taps an empty slot — so
 * embedding it is what makes teacher-scheduled privates work at all.
 *
 * **The toggle is off by default**, because her own classes are what she wants
 * nine times in ten. Switched on she gets the whole studio, which she is
 * entitled to: it is the timetable on the wall of the building she works in
 * (D-299).
 */
export function TeacherSchedule() {
  const queryClient = useQueryClient()

  /*
   * Which slice and which date, read from the address — **amending D-312**
   * (D-360).
   *
   * D-312 put this in `useState` because the switcher *navigated* to
   * `/calendar/day/…`, gated on `ViewAllSchedules`, so a teacher tapping "Day"
   * landed on *"Not your page"*. That reason is unchanged and still holds:
   * `onSelect` is what keeps her here, and nothing below navigates anywhere.
   * What changes is only *where the value is kept*.
   *
   * **It has to be somewhere both halves can read.** A teacher who is also a
   * parent moves between `/schedule/classes` and `/schedule/dancers` one tap
   * apart, and the children's half has read `?view=` and `?week=` from the
   * address since D-329. Local state here meant the two halves could not
   * possibly agree about which week she was looking at — the owner's question.
   * The same parameters, so the carry is the default rather than a feature.
   *
   * **`week` also carries a day and a month.** The name is D-329's and predates
   * the other two views; renaming it would break every link a parent has
   * already sent. It means *the anchor*, and the `view` beside it says what
   * unit to read it in.
   */
  const [params, setParams] = useSearchParams()

  const asked = params.get('view')
  const view: CalendarView =
    asked === 'day' || asked === 'month' || asked === 'week' ? asked : 'week'
  const date = params.get('week') ?? today()

  /*
   * Replaced rather than pushed, so paging four weeks forward does not leave
   * four entries in the back stack — the same call the children's half makes.
   */
  const setView = (next: { view: CalendarView; date: string }) => {
    const updated = new URLSearchParams(params)
    updated.set('view', next.view)
    updated.set('week', next.date)
    setParams(updated, { replace: true })
  }

  const setDate = (next: string) => setView({ view, date: next })

  const [declaringDay, setDeclaringDay] = useState(false)


  /*
   * A visible way to book a room (D-318).
   *
   * <b>Booking already worked and nobody could find it.</b> The only way in was
   * tapping an empty slot in the day grid — an invisible affordance, on a view
   * that is not the default, and on a phone there is no hover to reveal it. It
   * was reported as "I'm not sure how a teacher is supposed to search for an
   * open room and book a private", which is a discoverability fault rather than
   * a missing feature.
   *
   * The picker inside the form already answers *"what is free at this time?"*
   * and refuses a room that is not (D-301), so a button that opens it with no
   * room chosen is exactly the question being asked.
   */
  const [booking, setBooking] = useState<{ date: string; startMinutes: number } | null>(null)

  const canBook = useCan('BookRooms')

  // Is she somebody who teaches classes she could be out of (D-396). One
  // function, because the answer is subtler than `teaches` and getting it
  // wrong is invisible in the dev database — see `canDeclareOut`.
  const declaresOut = canDeclareOut(useSession().session)

  /*
   * **Ava's week holds her dancing too** (D-539), and this is the one line
   * that lets it.
   *
   * `teachingOnly` is D-374's opt-in: a teacher who is also a parent got her
   * daughter's ballet on the tab carrying her own name, reported twice by the
   * owner, so *My classes* stopped asking for the union. A dancer here is the
   * case that rule was never about — the union is not somebody else's child,
   * it is **her own five classes**, and she is the only reader for whom
   * leaving them out empties most of her week.
   *
   * **`&& !isParent` keeps D-374 exactly as it was.** Somebody who is a
   * teacher, a parent *and* a dancer gets the tab split and the teaching tab
   * stays narrow; her dancing is the one thing this does not solve for her,
   * and no such person exists at this studio today.
   */
  const { session: me } = useSession()
  const showsMyDancing = me?.isDancer === true && me?.isParent !== true

  const bookButton = canBook ? (
    <button
      type="button"
      onClick={() =>
        setBooking({
          date: view === 'month' ? today() : date,
          /*
           * 4:30pm is where the by-hand field opens, never what gets booked
           * (D-428). The studio opens then Monday to Thursday so it is the
           * likeliest starting point — but this button has no slot to name, and
           * `startChosen` defaults false, so the form asks her to pick before
           * anything can be saved. The old comment said "the form's own time
           * control moves it", which stopped being true the day that control
           * moved behind a link.
           */
          startMinutes: 16 * 60 + 30,
        })
      }
      /*
        `border-transparent` so this is exactly as tall as *I am out* beside it
        (D-383). Measured at 36px against its 38px — the filled button has no
        border to add the 2px the outlined one does. Small, but it is the same
        pair-alignment fault D-358 was reported for, found by measuring the
        parent row rather than by looking at this one.
      */
      className="flex items-center gap-1.5 rounded-lg border border-transparent bg-accent px-3 py-2 text-[13px] font-semibold text-on-accent hover:brightness-110"
    >
      <CalendarPlus size={15} strokeWidth={2.5} />
      Book a room
    </button>
  ) : null

  /*
   * Being out, in the control line beside booking a room (D-354, folding in the
   * owner's *"should prob be an action in line with the book a private/room
   * button"*). It sat inside `CoverPanel`'s header, two full-height components
   * down the page, which made the two things a teacher does to her own week
   * live in different places — the same move D-346 made for a parent's *Report
   * an absence* one screen over.
   *
   * Secondary to *Book a room*: booking is the everyday act, saying she is out
   * is the exception, and an outlined control beside a filled one says which is
   * which without a word.
   */
  /*
   * **Only somebody who has days here** (D-396).
   *
   * `asked`: *"I am out shouldn't be on tonyas view."* It was keyed on
   * `BookRooms`, which the owner holds along with everything else — so the
   * control offered her a dialog that could only ever list nothing, because
   * she teaches no classes to be out of.
   *
   * **The answer lives in `canDeclareOut`, and it is subtler than `teaches`.**
   * Getting it wrong is invisible from here: in the dev database Tonya teaches
   * nothing, so a guard written against `teaches` alone looks right locally —
   * and `observed` on production it is **true** for her, because she has
   * covered a session. A substitute keeps the control; the person whose view
   * is the whole studio does not.
   *
   * **Both, not either.** `BookRooms` is still what the cover endpoints are
   * gated on, so dropping it would offer a control the server refuses.
   */
  const outButton = canBook && declaresOut ? (
    <button
      type="button"
      onClick={() => setDeclaringDay(true)}
      className="flex items-center gap-1.5 rounded-lg border border-line-2 px-3 py-2 text-[13px] font-semibold text-ink-2 hover:border-ink-3"
    >
      <CalendarX2 size={15} strokeWidth={2.5} />
      {/*
        **It read *I am out*, which says a state and not an act** (D-396).
        `asked`: *"needs better wording."* Beside *Book a room* — a verb and its
        object — it was the odd one out, and it does not say what pressing it
        leads to. *Report time off* is the same shape as the parent's *Report an
        absence* one screen over, which is the same thing said by the other
        person.
      */}
      Report time off
    </button>
  ) : null

  const actions = (
    <div className="flex flex-wrap items-center gap-2">
      {bookButton}
      {outButton}
    </div>
  )

  return (
    <div className="w-full max-w-full">
      {booking && (
        <BookingForm
          // No room chosen: the form lists what is free and refuses what is not.
          roomId=""
          date={booking.date}
          startMinutes={booking.startMinutes}
          onClose={() => setBooking(null)}
        />
      )}

      {/*
        A whole day at once (D-354).

        **The same invalidation set the other two cover surfaces use**, and not
        the narrower one it started as. Declaring runs `ResolveNightAsync` per
        occurrence, so a substitute saying she is out comes off the night — and
        anything showing who is teaching it is then stale (D-330). Keying only
        on the cover list would leave the register naming somebody who has just
        said she is not coming, which is the defect D-330 exists to prevent.
      */}
      {declaringDay && (
        <DeclareOutDay
          onClose={() => setDeclaringDay(false)}
          onDone={() => refreshAfterDeclaring(queryClient)}
        />
      )}

      {view === 'week' && (
        <CalendarWeekView
          date={date}
          onDate={setDate}
          actions={actions}
          onView={(v, on) => setView({ view: v, date: on })}
          teachingOnly={!showsMyDancing}
        />
      )}

      {view === 'day' && (
        <CalendarDayView
          date={date}
          onDate={setDate}
          actions={actions}
          onView={(v, on) => setView({ view: v, date: on })}
          teachingOnly={!showsMyDancing}
        />
      )}

      {view === 'month' && (
        <CalendarMonthView
          date={date}
          onDate={setDate}
          actions={actions}
          onView={(v, on) => setView({ view: v, date: on })}
          teachingOnly={!showsMyDancing}
        />
      )}

    </div>
  )
}





/**
 * Her classes for the season, with the music on each.
 *
 * Behind her login, unlike the tablet's browser, because she is already signed
 * in — there is nothing left to open up (D-119).
 */
export function MyClasses() {
  const [day, setDay] = useState<string>('')
  const [rosterId, setRosterId] = useState<string | null>(null)

  const { data, isPending } = useQuery({
    queryKey: ['teacher-classes'],
    queryFn: fetchTeacherClasses,
  })

  if (isPending) return <p className="text-[13.5px] text-ink-3">Loading...</p>

  if (!data || data.length === 0) {
    return (
      <p className="rounded-2xl border border-line bg-surface px-5 py-8 text-center text-[14px] text-ink-3">
        No classes are assigned to you yet. Ask the studio if that looks wrong.
      </p>
    )
  }

  /*
   * The days she actually teaches, in week order, taken from her own classes.
   *
   * Not a fixed Monday-to-Sunday list: a teacher who is in on Tuesdays and
   * Thursdays gets two buttons, not seven with five that find nothing. The
   * studio runs Monday to Thursday with a little on Friday and Saturday
   * (`observed`: 38/49/33/38/1/6), so a fixed list would be mostly dead.
   */
  const order = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

  const days = [...new Set(data.map((c) => c.dayOfWeek))]
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))

  const shown = day ? data.filter((c) => c.dayOfWeek === day) : data

  return (
    <>
      {/*
        A day filter, because the list is one long scroll otherwise — 86 classes
        for the busiest teacher in the real data, and she is looking for one.
        Only shown once there is something to choose between.
      */}
      {days.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-2">
          <FilterChip on={day === ''} onClick={() => setDay('')}>
            All ({data.length})
          </FilterChip>

          {days.map((d) => (
            <FilterChip key={d} on={day === d} onClick={() => setDay(d)}>
              {d.slice(0, 3)} ({data.filter((c) => c.dayOfWeek === d).length})
            </FilterChip>
          ))}
        </div>
      )}

    <div className="grid gap-2.5 lg:grid-cols-2">
      {shown.map((studioClass) => (
        <div
          key={studioClass.studioClassId}
          className="room-tint flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-surface px-4 py-3.5"
          style={
            {
              '--room-color': studioClass.roomColor ?? 'var(--color-ink-3)',
              borderLeft: '3px solid var(--room-display)',
            } as React.CSSProperties
          }
        >
          {/*
            The card opens the roster (D-300).

            Reported from live testing: she could see a class and its headcount
            and had no way to ask who was in it. `RosterModal` already answers
            exactly that and was reachable only from the calendar.
          */}
          <button
            type="button"
            onClick={() => setRosterId(studioClass.studioClassId)}
            className="min-w-0 flex-1 text-left"
          >
            <p
              className="text-[14.5px] font-semibold wrap-anywhere"
              style={{ color: 'var(--room-display)' }}
            >
              {classLabel(studioClass)}
            </p>

            <p className="mt-0.5 flex flex-wrap items-center gap-x-2.5 text-[12.5px] text-ink-3">
              <span>{studioClass.dayOfWeek}</span>
              {studioClass.startTime && (
                <span className="tabular">
                  {clockTime(studioClass.startTime)}
                  {meridiem(studioClass.startTime)}
                </span>
              )}
              {studioClass.roomName && <span>{studioClass.roomName}</span>}
              <span className="flex items-center gap-1 tabular">
                <Users size={11} strokeWidth={2} />
                {studioClass.enrolled}
              </span>
            </p>
          </button>

          {studioClass.musicUrl ? (
            <a
              href={studioClass.musicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-line-2 px-3 py-2 text-[12.5px] font-semibold text-ink-2 no-underline"
            >
              <Music size={13} strokeWidth={2} />
              Music
            </a>
          ) : (
            <span className="shrink-0 text-[12px] text-ink-3">No music folder</span>
          )}

          {/*
            How many practice videos are on the class (D-541). A count rather
            than the links: the button above opens the class view, where they
            are listed and edited, and a pill per part would crowd a phone row.
          */}
          {(studioClass.choreoLinks?.length ?? 0) > 0 && (
            <span className="shrink-0 text-[12px] text-ink-3">
              {studioClass.choreoLinks.length === 1
                ? '1 practice video'
                : `${studioClass.choreoLinks.length} practice videos`}
            </span>
          )}
        </div>
      ))}
    </div>

      {rosterId && (
        <RosterModal kind="classList" id={rosterId} onClose={() => setRosterId(null)} />
      )}
    </>
  )
}

/** A day chip on the class list. Small, tappable, and states its count. */
function FilterChip({
  on,
  onClick,
  children,
}: {
  on: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`rounded-lg border px-3 py-1.5 text-[12.5px] font-semibold ${
        on ? 'border-accent bg-accent-soft text-accent' : 'border-line-2 text-ink-2'
      }`}
    >
      {children}
    </button>
  )
}
